import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";

const INPUT = "w-full bg-surface border border-hair rounded-full px-4 py-2.5 text-sm text-onSurface focus:outline-none focus:border-brand";
const MODE_TEXT = { auto: "Automatico: lo sceglie Readoo", manual: "Scelto da te", off: "Nascosto da te" };

// Admin: argomento del giorno (automatico, scelto a mano per 24 ore o nascosto per 24 ore).
function TopicAdmin() {
  const [t, setT] = useState(null);
  const [form, setForm] = useState({ label: "", lead_url: "", related: "" });
  const [busy, setBusy] = useState(false);
  const load = () => apiFetch(`/admin/engagement/topic`, { auth: true }).then(setT).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async (body) => {
    setBusy(true);
    try { setT(await apiFetch(`/admin/engagement/topic`, { method: "PUT", auth: true, body })); toast.success("Argomento del giorno aggiornato"); }
    catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(false); }
  };
  const saveManual = () => send({ mode: "manual", label: form.label, lead_url: form.lead_url,
    related_urls: form.related.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 4) });
  if (!t) return <Loader2 className="animate-spin text-subtle" />;
  return (
    <section data-testid="topic-admin">
      <h2 className="text-lg font-semibold text-onSurface mb-1">Argomento del giorno</h2>
      <p data-testid="topic-admin-status" className="text-sm text-subtle mb-3">
        {MODE_TEXT[t.mode]}{t.until ? ` fino alle ${new Date(t.until).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} di domani` : ""}
        {" · "}{t.topic ? <>in home ora: <strong className="text-onSurface">{t.topic.label}</strong> — {t.topic.lead.title}</> : "oggi non compare in home"}
      </p>
      <div className="space-y-2">
        <input data-testid="topic-admin-label" placeholder="Nome dell'argomento (es. Elezioni Usa)" maxLength={40} value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })} className={INPUT} />
        <input data-testid="topic-admin-lead" placeholder="Link dell'articolo principale" value={form.lead_url}
          onChange={(e) => setForm({ ...form, lead_url: e.target.value })} className={INPUT} />
        <textarea data-testid="topic-admin-related" placeholder="Link degli articoli collegati, uno per riga (massimo 4: se ne metti meno, Readoo completa con le notizie delle ultime 24 ore o le scrive)" rows={3} value={form.related}
          onChange={(e) => setForm({ ...form, related: e.target.value })} className={`${INPUT} rounded-2xl`} />
        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="topic-admin-save" disabled={busy || form.label.length < 3 || !form.lead_url} onClick={saveManual}
            className="px-5 py-2.5 rounded-full bg-brand text-onBrand text-sm font-semibold disabled:opacity-50">Usa questo per 24 ore</button>
          <button type="button" data-testid="topic-admin-off" disabled={busy} onClick={() => send({ mode: "off" })}
            className="px-5 py-2.5 rounded-full border border-hair text-sm font-semibold text-onSurface disabled:opacity-50">Nascondi per 24 ore</button>
          <button type="button" data-testid="topic-admin-auto" disabled={busy || t.mode === "auto"} onClick={() => send({ mode: "auto" })}
            className="px-5 py-2.5 rounded-full border border-hair text-sm font-semibold text-onSurface disabled:opacity-50">Torna automatico</button>
        </div>
      </div>
    </section>
  );
}

const LABELS = {
  radar_viewed: "Radar visto", radar_trend_clicked: "Radar: clic su un trend",
  daily_brief_viewed: "5 cose visto", daily_brief_article_clicked: "5 cose: clic su un articolo",
  poll_viewed: "Domanda vista", poll_voted: "Domanda: voti",
  on_this_day_viewed: "Accadde oggi visto", on_this_day_clicked: "Accadde oggi: clic",
};

// Admin: contatori dei moduli di engagement (ultimi 7 giorni) e modifica della Domanda di oggi.
export function EngagementTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => apiFetch(`/admin/engagement/stats?days=7`, { auth: true }).then((d) => { setData(d); setQ(d.poll?.question || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try { await apiFetch(`/admin/engagement/poll`, { method: "PUT", auth: true, body: { question: q } }); toast.success("Domanda aggiornata"); load(); }
    catch (e) { toast.error(e.message || "Errore"); } finally { setSaving(false); }
  };
  if (!data) return <Loader2 className="animate-spin text-subtle" />;
  return (
    <div data-testid="engagement-tab" className="max-w-3xl space-y-8">
      <TopicAdmin />
      <section>
        <h2 className="text-lg font-semibold text-onSurface mb-1">La domanda di oggi</h2>
        <p className="text-sm text-subtle mb-3">{data.poll ? `Proposta ${data.poll.total ? `· ${data.poll.total} voti` : "· nessun voto ancora"}` : "Non ancora creata: puoi scriverla tu"}</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input data-testid="engagement-poll-input" value={q} onChange={(e) => setQ(e.target.value)} maxLength={140}
            className="flex-1 bg-surface border border-hair rounded-full px-4 py-2.5 text-sm text-onSurface focus:outline-none focus:border-brand" />
          <button type="button" data-testid="engagement-poll-save" disabled={saving || q.length < 8} onClick={save}
            className="px-5 py-2.5 rounded-full bg-brand text-onBrand text-sm font-semibold disabled:opacity-50">Salva domanda</button>
        </div>
      </section>
      <section>
        <h2 className="text-lg font-semibold text-onSurface mb-3">Utilizzo dei moduli · ultimi 7 giorni</h2>
        <ul className="divide-y divide-hair border border-hair rounded-xl overflow-hidden">
          {Object.entries(data.totals).map(([k, v]) => (
            <li key={k} data-testid={`engagement-stat-${k}`} className="flex justify-between px-4 py-2.5 bg-surface text-sm">
              <span className="text-onSurface">{LABELS[k] || k}</span><span className="font-mono tabular-nums text-onSurface">{v}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
